import React, { useEffect, useState, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { PropTypes } from 'prop-types';
import injectSheet from 'react-jss';
import { Loading, Button } from '@spoiler-alert/ui-library';
import { TitleService } from '../../services';
import { Breadcrumbs } from '../../store';
import ExportManagerNavigation from './em-navigation';
import NewExportProfile from './new-export-profile';
import EditExportProfileModal from './edit-export-profile-modal';
import ExportMappingsTable from './export-mappings-table';
import MultilineHeadersTable from './multiline-headers-table';
import {
  EXPORT_PROFILE_FIELDS_QUERY,
  CREATE_OFFER_EXPORT_PROFILE_MUTATION,
  CREATE_AWARD_EXPORT_PROFILE_MUTATION,
  CREATE_MARKETPLACE_AUCTION_EXPORT_PROFILE_MUTATION,
  CREATE_EXPORT_MAPPING_MUTATION,
  UPDATE_EXPORT_PROFILE_MUTATION,
  UPDATE_EXPORT_MAPPING_MUTATION,
  UPDATE_MAPPINGS_ORDER_MUTATION,
  DELETE_EXPORT_MAPPING_MUTATION,
  CREATE_NEGOTIATION_EXPORT_PROFILE_MUTATION,
  CREATE_SALES_HISTORY_EXPORT_PROFILE_MUTATION,
  CREATE_AWARDING_OPPORTUNITIES_EXPORT_PROFILE_MUTATION,
} from './export-queries';
import ETLErrors from '../import-manager/etl-errors';
import ProfileUploadConfirmationModal from '../../components/modals/profile-upload-confirmation-modal';
import AppSettings from '../../app-settings';
import EXPORT_TYPE_OPTIONS from './enums/export-types';
import useProfileTransfer from '../../lib/profile-transfer/use-profile-transfer';
import { buildExportProfileDocument } from '../../lib/profile-transfer/serialize';
import { PROMOTE_EXPORT_PROFILE_MUTATION } from '../../lib/profile-transfer/profile-transfer-queries';
import { PROFILE_KINDS } from '../../lib/profile-transfer/profile-document';

const styles = {
  wrap: {
    overflow: 'auto',
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    marginBottom: 25,
    alignItems: 'center',
  },
  tableName: {
    width: '100%',
    fontWeight: 500,
    fontSize: '14px',
    marginBottom: '5px',
  },
  pageButtons: {
    display: 'flex',
    flexDirection: 'row',
    '&>button': {
      marginLeft: 10,
    },
  },
};

const ExportManager = ({ classes }) => {
  const [exportType, setExportType] = useState(EXPORT_TYPE_OPTIONS.OFFER);
  const [site, setSite] = useState({ _id: null, siteName: null });
  const [exportProfile, setExportProfile] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [sheetWriters, setSheetWriters] = useState([]);

  useEffect(() => {
    TitleService.setTitles('ExportManager');
    Breadcrumbs.set([
      {
        url: '/export-manager',
        title: 'Export Manager',
      },
    ]);
  });

  const {
    data: profileFields,
    loading: loadingFields,
    error: loadFieldsError,
  } = useQuery(EXPORT_PROFILE_FIELDS_QUERY, {
    variables: {
      exportProfileId: exportProfile?._id,
    },
    skip: !exportProfile,
  });

  const getPossibleColors = () =>
    profileFields?.exportManagerQuery?.exportProfile?.possibleColors?.map((color) => ({
      name: color.name.replace(/\w+/g, (name) => name.charAt(0).toUpperCase() + name.slice(1)),
      hex: color.hex,
    }));

  const possibleColors = useMemo(
    () => getPossibleColors(),
    [profileFields?.exportManagerQuery?.exportProfile?.possibleColors],
  );

  const [createOfferExportProfile, { loading: creatingOfferExportProfile, error: createOfferExportProfileError }] =
    useMutation(CREATE_OFFER_EXPORT_PROFILE_MUTATION, {
      onCompleted: (data) => {
        setExportProfile(data.createOfferExportProfile);
      },
      refetchQueries: ['getSitesAndExportProfiles'],
    });
  const [createAwardExportProfile, { loading: creatingAwardExportProfile, error: createAwardExportProfileError }] =
    useMutation(CREATE_AWARD_EXPORT_PROFILE_MUTATION, {
      onCompleted: (data) => {
        setExportProfile(data.createAwardExportProfile);
      },
      refetchQueries: ['getSitesAndExportProfiles'],
    });
  const [
    createNegotiationExportProfile,
    { loading: creatingNegotiationExportProfile, error: createNegotiationExportProfileError },
  ] = useMutation(CREATE_NEGOTIATION_EXPORT_PROFILE_MUTATION, {
    onCompleted: (data) => {
      setExportProfile(data.createNegotiationExportProfile);
    },
    refetchQueries: ['getSitesAndExportProfiles'],
  });
  const [
    createMarketplaceAuctionExportProfile,
    { loading: creatingMarketplaceAuctionExportProfile, error: createMarketplaceAuctionExportProfileError },
  ] = useMutation(CREATE_MARKETPLACE_AUCTION_EXPORT_PROFILE_MUTATION, {
    onCompleted: (data) => {
      setExportProfile(data.createMarketplaceAuctionExportProfile);
    },
    refetchQueries: ['getSitesAndExportProfiles'],
  });
  const [
    createSalesHistoryExportProfile,
    { loading: creatingSalesHistoryExportProfile, error: createSalesHistoryExportProfileError },
  ] = useMutation(CREATE_SALES_HISTORY_EXPORT_PROFILE_MUTATION, {
    onCompleted: (data) => {
      setExportProfile(data.createSalesHistoryExportProfile);
    },
    refetchQueries: ['getSitesAndExportProfiles'],
  });
  const [
    createAwardingOpportunitiesExportProfile,
    { loading: creatingAwardingOpportunitiesExportProfile, error: createAwardingOpportunitiesExportProfileError },
  ] = useMutation(CREATE_AWARDING_OPPORTUNITIES_EXPORT_PROFILE_MUTATION, {
    onCompleted: (data) => {
      setExportProfile(data.createAwardingOpportunitiesExportProfile);
    },
    refetchQueries: ['getSitesAndExportProfiles'],
  });
  const [createExportMapping, { loading: creatingMapping, error: createMappingError }] =
    useMutation(CREATE_EXPORT_MAPPING_MUTATION);
  const [updateExportMapping, { data: updatedMapping, loading: savingMapping, error: saveMappingError }] =
    useMutation(UPDATE_EXPORT_MAPPING_MUTATION);
  const [updateMappingsOrder, { loading: savingMappingsOrder, error: updateMappingsOrderError }] =
    useMutation(UPDATE_MAPPINGS_ORDER_MUTATION);
  const [deleteExportMapping, { loading: deletingMapping, error: deleteMappingError }] =
    useMutation(DELETE_EXPORT_MAPPING_MUTATION);
  const [updateExportProfile, { loading: savingMultilineHeader, error: saveProfileError }] = useMutation(
    UPDATE_EXPORT_PROFILE_MUTATION,
    {
      onCompleted: (data) => {
        if (data?.updateExportProfile) setExportProfile(data.updateExportProfile);
      },
      refetchQueries: ['getSitesAndExportProfiles', 'getFieldsByExportProfileId'],
    },
  );

  const profileMutations = {
    createOfferExportProfile,
    createAwardExportProfile,
    createNegotiationExportProfile,
    createMarketplaceAuctionExportProfile,
    createSalesHistoryExportProfile,
    createAwardingOpportunitiesExportProfile,
  };

  const mappingMutations = {
    createExportMapping,
    updateExportMapping,
    deleteExportMapping,
    updateMappingsOrder,
  };

  const multilineHeaderMutations = {
    updateExportProfile,
  };

  const errors = [
    loadFieldsError,
    createOfferExportProfileError,
    createAwardExportProfileError,
    createNegotiationExportProfileError,
    createMarketplaceAuctionExportProfileError,
    createSalesHistoryExportProfileError,
    createMappingError,
    saveProfileError,
    saveMappingError,
    deleteMappingError,
    updateMappingsOrderError,
    createAwardingOpportunitiesExportProfileError,
  ];

  const loading = () =>
    loadingFields ||
    creatingOfferExportProfile ||
    creatingAwardExportProfile ||
    creatingNegotiationExportProfile ||
    creatingMarketplaceAuctionExportProfile ||
    creatingSalesHistoryExportProfile ||
    creatingAwardingOpportunitiesExportProfile;

  const { download, requestUpload, pendingUpload, confirmUpload, cancelUpload, uploading, fileInputProps } =
    useProfileTransfer({
      kind: PROFILE_KINDS.EXPORT,
      identity: { exportType },
      target: { siteId: site._id, exportType },
      buildDocument: (exportedAt) =>
        buildExportProfileDocument({
          environment: AppSettings.ENVIRONMENT_NAME,
          site,
          exportProfile,
          mappings: profileFields?.exportManagerQuery?.exportProfile?.mappings,
          exportedAt,
        }),
      mutation: PROMOTE_EXPORT_PROFILE_MUTATION,
      resultKey: 'promoteExportProfile',
      refetchQueries: ['getSitesAndExportProfiles', 'getFieldsByExportProfileId'],
      onPromoted: (result) => setExportProfile(result.profile),
    });

  const possibleFormatters = useMemo(() => {
    if (!profileFields) return [];
    if (exportType === EXPORT_TYPE_OPTIONS.OFFER || exportType === EXPORT_TYPE_OPTIONS.MARKETPLACE_AUCTION)
      return profileFields.exportManagerQuery.exportProfile.possibleOfferExportFormatters;
    if (exportType === EXPORT_TYPE_OPTIONS.NEGOTIATION)
      return profileFields.exportManagerQuery.exportProfile.possibleNegotiationExportFormatters;
    if (exportType === EXPORT_TYPE_OPTIONS.AWARD)
      return profileFields.exportManagerQuery.exportProfile.possibleAwardExportFormatters;
    if (exportType === EXPORT_TYPE_OPTIONS.SALES_HISTORY)
      return profileFields.exportManagerQuery.exportProfile.possibleSalesHistoryExportFormatters;
    if (exportType === EXPORT_TYPE_OPTIONS.AWARDING_OPPORTUNITIES)
      return profileFields.exportManagerQuery.exportProfile.possibleAwardingOpportunitiesExportFormatters;
    return [];
  }, [exportType, profileFields]);

  const renderView = () => {
    if (!site._id) {
      return <div />;
    }
    if (profileFields) {
      return (
        <>
          {(exportType === EXPORT_TYPE_OPTIONS.AWARD || exportType === EXPORT_TYPE_OPTIONS.MARKETPLACE_AUCTION) && (
            <>
              <span className={classes.tableName}>Multi-Line Headers</span>
              <MultilineHeadersTable
                exportProfile={exportProfile}
                mutations={multilineHeaderMutations}
                possibleColors={possibleColors}
                loading={savingMultilineHeader}
                site={site}
              />
            </>
          )}
          <span className={classes.tableName}>Columns</span>
          <ExportMappingsTable
            exportProfile={exportProfile}
            mutations={mappingMutations}
            mappings={profileFields.exportManagerQuery.exportProfile.mappings}
            possibleInternalFields={profileFields.exportManagerQuery.exportProfile.possibleInternalFields}
            possibleFormatters={possibleFormatters}
            possibleColors={possibleColors}
            updatedRecord={updatedMapping}
            loading={creatingMapping || savingMapping || deletingMapping || savingMappingsOrder}
          />
        </>
      );
    }
    if (!exportProfile) {
      return (
        <NewExportProfile
          exportType={exportType}
          site={site}
          mutations={profileMutations}
          possibleSheetWriters={sheetWriters}
        />
      );
    }
    return <div />;
  };

  return (
    <div className={classes.wrap}>
      <header className={classes.header}>
        <ExportManagerNavigation
          site={site}
          setSite={setSite}
          setExportProfile={setExportProfile}
          exportType={exportType}
          setExportType={setExportType}
          setSheetWriters={setSheetWriters}
        />

        <div className={classes.pageButtons}>
          {exportType && site._id && (
            <Button primary onClick={requestUpload} disabled={!!loadFieldsError || loading() || uploading}>
              Upload Profile
            </Button>
          )}
          {exportProfile && (
            <>
              <Button primary onClick={download} disabled={!!loadFieldsError || loading()}>
                Download Profile
              </Button>
              <Button secondary onClick={() => setShowEditModal(true)}>
                Settings
              </Button>
            </>
          )}
        </div>
      </header>
      {/* Mounted unconditionally: requestUpload() clicks this hidden input, so it must exist even
          when no profile is selected -- which is exactly when you would import one. The modal
          renders nothing until there is a pending upload. */}
      <ProfileUploadConfirmationModal
        pendingUpload={pendingUpload}
        targetDescription={`${site.siteName} — ${exportType}`}
        onConfirm={confirmUpload}
        onCancel={cancelUpload}
        uploading={uploading}
      />
      <input {...fileInputProps} />
      <ETLErrors errors={errors} />
      <Loading loading={loading()}>{renderView()}</Loading>
      {profileFields && (
        <EditExportProfileModal
          showEditModal={showEditModal}
          setShowEditModal={setShowEditModal}
          exportProfile={exportProfile}
          updateExportProfile={updateExportProfile}
          possibleSheetWriters={sheetWriters}
        />
      )}
    </div>
  );
};

ExportManager.propTypes = {
  classes: PropTypes.object,
};

export default injectSheet(styles)(ExportManager);
